import React, { useState } from 'react';
import { X, Building2, FileText, Mail, Phone, AlertCircle, User } from 'lucide-react';
import { TextInput } from '../../../shared/components/TextInput';
import { maskCNPJ, maskPhone } from '../../../shared/utils/validators';
import { useAtualizarEmpresa } from '../hooks';
import type { Company } from '../../../shared/lib/api';
import { BankAccountsSection } from './BankAccountsSection';
import {
  normalizeCompanyForm,
  validateCompanyForm,
  type CompanyFormValues,
} from '../validation/companyForms';

interface Props {
  company: Company;
  onClose: () => void;
  onSuccess: () => void;
}

export const CompanyEditModal: React.FC<Props> = ({ company, onClose, onSuccess }) => {
  const [formData, setFormData] = useState<CompanyFormValues>({
    corporateName: company.corporateName,
    tradeName: company.tradeName,
    cnpj: maskCNPJ(company.cnpj),
    responsibleName: company.responsibleName,
    responsibleEmail: company.responsibleEmail,
    responsiblePhone: company.responsiblePhone,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const atualizarEmpresa = useAtualizarEmpresa();

  const validate = (): boolean => {
    const newErrors = validateCompanyForm(formData);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field: keyof CompanyFormValues, value: string) => {
    let processed = value;
    if (field === 'cnpj') processed = maskCNPJ(value);
    else if (field === 'responsiblePhone') processed = maskPhone(value);
    setFormData(prev => ({ ...prev, [field]: processed }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setIsLoading(true);
    try {
      await atualizarEmpresa.mutateAsync({ id: company.id, payload: normalizeCompanyForm(formData) });
      onSuccess();
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Erro ao atualizar empresa.';
      setErrors(prev => ({ ...prev, submit: message }));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-bold text-gray-900">Editar Empresa</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <TextInput
            label="Razão Social *"
            icon={<FileText className="w-5 h-5" />}
            value={formData.corporateName}
            onChange={e => handleChange('corporateName', e.target.value)}
            error={errors.corporateName}
          />

          <TextInput
            label="Nome Fantasia *"
            icon={<Building2 className="w-5 h-5" />}
            value={formData.tradeName}
            onChange={e => handleChange('tradeName', e.target.value)}
            error={errors.tradeName}
          />

          <TextInput
            label="CNPJ *"
            icon={<FileText className="w-5 h-5" />}
            value={formData.cnpj}
            onChange={e => handleChange('cnpj', e.target.value)}
            error={errors.cnpj}
            maxLength={18}
            inputMode="text"
          />

          <TextInput
            label="Nome do Responsável *"
            icon={<User className="w-5 h-5" />}
            value={formData.responsibleName}
            onChange={e => handleChange('responsibleName', e.target.value)}
            error={errors.responsibleName}
          />

          <TextInput
            label="E-mail do Responsável *"
            type="email"
            icon={<Mail className="w-5 h-5" />}
            value={formData.responsibleEmail}
            onChange={e => handleChange('responsibleEmail', e.target.value)}
            error={errors.responsibleEmail}
          />

          <TextInput
            label="Telefone do Responsável *"
            icon={<Phone className="w-5 h-5" />}
            value={formData.responsiblePhone}
            onChange={e => handleChange('responsiblePhone', e.target.value)}
            error={errors.responsiblePhone}
            maxLength={16}
            inputMode="numeric"
          />

          <div className="border-t border-gray-200 pt-5">
            <BankAccountsSection companyId={company.id} />
          </div>

          {errors.submit && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600 flex items-center gap-2">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {errors.submit}
              </p>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className={`px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {isLoading ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
